import React, { useEffect, useState, useMemo } from "react";
import "./styles/Reports.css";
import { apiService } from "./services/apiService";
import * as XLSX from "xlsx"; 
import { FaDownload, FaFileExcel, FaEnvelope, FaSort, FaSortUp, FaSortDown } from "react-icons/fa"; 

function Reports({ onBack }) {
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  
  // State to manage sorting configuration
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // 🆕 State to manage the Registration Number filter (Client-Side)
  const [filterRegNo, setFilterRegNo] = useState("");

  useEffect(() => {
    apiService.getAllAdmissions()
      .then((data) => { setAdmissions(data || []); setLoading(false); })
      .catch((err) => { console.error(err); setLoading(false); });
  }, []);

  const currentTimestamp = new Date().toLocaleString("en-GB", { hour12: true });

  // Sorting Handler logic
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Memoized sorted & filtered array calculation safely resolving nested keys
  const sortedAdmissions = useMemo(() => {
    let sortableItems = [...admissions];

    // 🆕 Client-Side Filtering by Registration No (Without hitting API again)
    if (filterRegNo.trim() !== "") {
      const searchTerm = filterRegNo.trim().toLowerCase();
      sortableItems = sortableItems.filter((ad) => 
        String(ad.student?.registrationNo || "").toLowerCase().includes(searchTerm)
      );
    }

    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;

        // Safely extract deeply nested attributes (e.g. 'student.fullName')
        if (sortConfig.key.includes(".")) {
          const parts = sortConfig.key.split(".");
          aValue = parts.reduce((obj, part) => obj?.[part], a);
          bValue = parts.reduce((obj, part) => obj?.[part], b);
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        // Keep null/undefined items consistently positioned at the bottom
        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;

        // Dedicated numeric parsing rule for feesPaid values
        if (sortConfig.key === "feesPaid") {
          return sortConfig.direction === "asc" 
            ? Number(aValue) - Number(bValue) 
            : Number(bValue) - Number(aValue);
        }

        // String and localized date format comparisons
        const stringA = String(aValue).toLowerCase();
        const stringB = String(bValue).toLowerCase();

        if (stringA < stringB) return sortConfig.direction === "asc" ? -1 : 1;
        if (stringA > stringB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [admissions, sortConfig, filterRegNo]); // 🔄 Added filterRegNo to dependencies

  // 🔄 Updated totalAmount to calculate from sortedAdmissions so table footers & exports remain accurate to the filtered view
  const totalAmount = sortedAdmissions.reduce((sum, ad) => sum + (Number(ad.feesPaid) || 0), 0);

  // Renders correct active sorting icons contextually inside column th blocks
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort style={{ marginLeft: "6px", opacity: 0.4 }} />;
    return sortConfig.direction === "asc" 
      ? <FaSortUp style={{ marginLeft: "6px", color: "#007bff" }} /> 
      : <FaSortDown style={{ marginLeft: "6px", color: "#007bff" }} />;
  };

  // PDF Generation Helper reading the sorted array structure explicitly
  const generatePDFBlob = async () => {
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text("Student Admission Fee Report", 14, 15);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated On: ${currentTimestamp}`, 14, 22);
    
    const tableBody = sortedAdmissions.map((ad, i) => [
      i + 1, 
      ad.student?.registrationNo || "", 
      ad.student?.fullName || "", 
      ad.seat?.seatCode || "", 
      ad.startDate || "", 
      ad.endDate || "", 
      ad.feesPaid != null ? `${ad.feesPaid}` : "—"
    ]);
    
    tableBody.push(["", "", "", "", "", "Total Amount:", `${totalAmount}`]);

    autoTable(doc, {
      startY: 28,
      head: [["#", "Registration No", "Student Name", "Seat Code", "Start Date", "End Date", "Fees Paid"]],
      body: tableBody
    });
    return doc.output("blob");
  };

  // Excel Format Structuring Helper aligned to sorted data flows
  const prepareExcelData = () => {
    const dataRows = sortedAdmissions.map((ad, i) => ({
      "#": i + 1,
      "Registration No": ad.student?.registrationNo || "",
      "Student Name": ad.student?.fullName || "",
      "Seat Code": ad.seat?.seatCode || "",
      "Start Date": ad.startDate || "",
      "End Date": ad.endDate || "",
      "Fees Paid": ad.feesPaid || 0
    }));

    dataRows.push({
      "#": "", "Registration No": "", "Student Name": "", "Seat Code": "", "Start Date": "",
      "End Date": "Total Amount:", "Fees Paid": totalAmount
    });
    dataRows.push({
      "#": "", "Registration No": "", "Student Name": "", "Seat Code": "", "Start Date": "",
      "End Date": "Generated At:", "Fees Paid": currentTimestamp
    });

    return dataRows;
  };

  const handleDownloadXls = () => {
    const worksheet = XLSX.utils.json_to_sheet(prepareExcelData());
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Admissions");
    XLSX.writeFile(workbook, "Admission-Report.xlsx");
  };

  const handleEmailBoth = async () => {
    setEmailing(true);
    try {
      const pdfBlob = await generatePDFBlob();
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prepareExcelData()), "Admissions");
      const xlsBlob = new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

      const fd = new FormData();
      fd.append("pdf", pdfBlob, "Report.pdf");
      fd.append("xlsx", xlsBlob, "Report.xlsx");
      fd.append("reportType", "ADMISSION_FEE_REPORT");
      
      await apiService.emailReport(fd);
      alert("Email sent successfully!");
    } catch (err) { alert("Email failed."); }
    setEmailing(false);
  };

  return (
    <div className="rp-page">
      <div className="rp-header-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 className="rp-title" style={{ margin: 0 }}>Student Admission Fee Report</h2>
          <div className="rp-timestamp" style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
            Generated On: <b>{currentTimestamp}</b>
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {admissions.length > 0 && (
            <>
              <button onClick={() => generatePDFBlob().then(b => { 
                const url = URL.createObjectURL(b); 
                const a = document.createElement('a'); a.href = url; a.download = "Report.pdf"; a.click(); 
              })} className="rp-btn-back"><FaDownload /> PDF</button>
              <button onClick={handleDownloadXls} className="rp-btn-back"><FaFileExcel /> Excel</button>
              <button onClick={handleEmailBoth} disabled={emailing} className="rp-btn-back"><FaEnvelope /> {emailing ? "Sending..." : "Email Both"}</button>
            </>
          )}
          <button onClick={onBack} className="rp-btn-back">Back To Dashboard</button>
        </div>
      </div>
      
      <div className="rp-card">
        {/* 🆕 Clean, non-intrusive Search / Filter Bar added seamlessly above the table */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", gap: "10px", background: "#fafafa" }}>
          <label style={{ fontWeight: "600", fontSize: "14px", color: "#444" }}>
            Filter Candidate:
          </label>
          <input
            type="text"
            placeholder="Enter Registration No..."
            value={filterRegNo}
            onChange={(e) => setFilterRegNo(e.target.value)}
            style={{
              padding: "6px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "14px",
              width: "240px",
              outline: "none"
            }}
          />
          {filterRegNo && (
            <button
              onClick={() => setFilterRegNo("")}
              style={{
                background: "transparent",
                border: "none",
                color: "#d32f2f",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "600",
                textDecoration: "underline",
                padding: "0 4px"
              }}
            >
              Clear Filter
            </button>
          )}
        </div>

        <div className="rp-table-wrapper">
          <table className="rp-table">
            <thead>
              <tr>
                <th style={{ userSelect: "none" }}>#</th>
                <th onClick={() => handleSort("student.registrationNo")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Registration No {renderSortIcon("student.registrationNo")}
                </th>
                <th onClick={() => handleSort("student.fullName")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Student Name {renderSortIcon("student.fullName")}
                </th>
                <th onClick={() => handleSort("seat.seatCode")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Seat Code {renderSortIcon("seat.seatCode")}
                </th>
                <th onClick={() => handleSort("startDate")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Start {renderSortIcon("startDate")}
                </th>
                <th onClick={() => handleSort("endDate")} style={{ cursor: "pointer", userSelect: "none" }}>
                  End {renderSortIcon("endDate")}
                </th>
                <th onClick={() => handleSort("feesPaid")} style={{ cursor: "pointer", userSelect: "none" }}>
                  Fees {renderSortIcon("feesPaid")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedAdmissions.length > 0 ? (
                sortedAdmissions.map((ad, index) => (
                  <tr key={ad.id}>
                    <td>{index + 1}</td>
                    <td>{ad.student?.registrationNo}</td>
                    <td className="rp-td-name">{ad.student?.fullName}</td>
                    <td><span className="rp-seat-badge">{ad.seat?.seatCode}</span></td>
                    <td>{ad.startDate}</td>
                    <td>{ad.endDate}</td>
                    <td className="rp-td-fees">{ad.feesPaid}</td>
                  </tr>
                ))
              ) : (
                /* 🆕 Graceful empty-state if someone types a non-existent Registration No */
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                    No student admissions found matching Registration No: <b>"{filterRegNo}"</b>
                  </td>
                </tr>
              )}
            </tbody>
            {sortedAdmissions.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="6" style={{ textAlign: "right", fontWeight: "bold", background: "#f8f9fa" }}>Total Amount:</td>
                  <td className="rp-td-fees" style={{ fontWeight: "bold", background: "#f8f9fa", color: "#2e7d32" }}>{totalAmount}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

export default Reports;